import {createContext, useContext, ReactNode} from 'react';

import {ApiError} from '~/shared/services/apiErrorService';
import {MoneyCard} from '~/shared/services/bffService/api/interfaces/responses';
import usePayments from '~/common/hooks/usePayments';
import {UserCreditCard, UserPaypal} from '~/personalArea/components/Sections/Payments/types';

interface PersonalAreaContextValue {
  moneycards: MoneyCard[] | null;
  payments?: (UserCreditCard | UserPaypal)[] | null;
  isLoaded: boolean;
  isLoading: boolean;
  removePaymentMethodHandler: (args: {
    id: number;
    type: string;
  }) => Promise<boolean>;
  getUserPayments: () => Promise<void>;
  tenbisCreditBalance: number;
  activateMoneyCardById: (id: string) => void;
  moneyCardsError: ApiError | null;
}

const PersonalAreaContext = createContext<PersonalAreaContextValue | undefined>(undefined);

export const usePersonalAreaContext = () => {
  const context = useContext(PersonalAreaContext);
  if (!context) {
    throw new Error('usePersonalAreaContext must be used within PersonalAreaProvider');
  }
  return context;
};

interface PersonalAreaProviderProps {
  children: ReactNode;
}

export const PersonalAreaProvider = ({children}: PersonalAreaProviderProps) => {
  const {
    payments,
    moneyCards,
    isLoaded,
    isLoading,
    removePaymentMethodHandler,
    getUserPayments,
    tenbisCreditBalance,
    activateMoneyCardById,
    error: moneyCardsError,
  } = usePayments();

  const value: PersonalAreaContextValue = {
    moneycards: moneyCards,
    payments,
    isLoaded,
    isLoading,
    removePaymentMethodHandler,
    getUserPayments,
    tenbisCreditBalance,
    activateMoneyCardById,
    moneyCardsError,
  };

  return (
    <PersonalAreaContext.Provider value={value}>
      {children}
    </PersonalAreaContext.Provider>
  );
};
